import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { RefreshCw, Database, AlertTriangle } from "lucide-react";

const REASON_LABELS = {
  not_on_plan: "Not on your FMP plan",
  limit_reached: "Daily limit reached",
  rate_limited: "FMP busy (retries ran out)",
  fmp_error: "FMP error",
  no_data: "FMP had no data",
  no_key: "No FMP key set",
  partial: "Some details filled from backup",
};

const fmtTime = (iso) =>
  iso ? `${new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC" })} UTC` : "—";

function UsageMeter({ data }) {
  const pct = Math.min(100, Math.round((data.calls / Math.max(data.daily_limit, 1)) * 100));
  return (
    <div className="rounded-lg border border-border p-5" data-testid="data-usage-meter">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">FMP lookups today (UTC)</div>
          <div className="text-3xl font-semibold tabular-nums mt-1" data-testid="data-usage-calls">
            {data.calls}<span className="text-base text-muted-foreground font-normal"> / {data.daily_limit}</span>
          </div>
        </div>
        {data.limit_reached ? (
          <Badge variant="destructive" data-testid="data-usage-limit-badge">Limit reached — using backup</Badge>
        ) : (
          <Badge variant="outline" data-testid="data-usage-remaining">{data.remaining} left</Badge>
        )}
      </div>
      <Progress value={data.limit_reached ? 100 : pct} className="mt-4 h-2" data-testid="data-usage-progress" />
      {data.limit_reached && data.calls < data.daily_limit && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="data-usage-mismatch-note">
          FMP says today's limit is used up. Some lookups happened outside this counter, for example before tracking started or with the same key elsewhere.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
        <Stat label="Served from saved data" value={data.cache_hits} testid="data-usage-cache-hits" />
        <Stat label="Resets" value={fmtTime(data.resets_at)} testid="data-usage-resets" />
        {data.limit_reached && <Stat label="FMP paused until" value={fmtTime(data.limit_reached_until)} testid="data-usage-paused-until" />}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}

function FallbackTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground py-6 text-center" data-testid="data-usage-no-fallbacks">No tickers used the free backup today.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="data-usage-fallback-table">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2 pr-4 font-medium">Ticker</th>
            <th className="py-2 pr-4 font-medium">Times</th>
            <th className="py-2 font-medium">Why</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ticker} className="border-b border-border/60 align-top" data-testid={`data-usage-row-${r.ticker}`}>
              <td className="py-2 pr-4 mono font-medium">{r.ticker}</td>
              <td className="py-2 pr-4 tabular-nums">{r.total}</td>
              <td className="py-2">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(r.reasons).map(([k, n]) => (
                    <Badge key={k} variant="secondary" className="font-normal">{REASON_LABELS[k] || k} · {n}</Badge>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DataUsageCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/data-usage");
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load data usage"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="mt-6" data-testid="data-usage-card">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="serif text-2xl flex items-center gap-2"><Database className="w-5 h-5 text-primary" /> Market data usage</h2>
            <p className="text-sm text-muted-foreground">Today's Financial Modeling Prep lookups and the tickers that fell back to Yahoo Finance.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="data-usage-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        {!data && <div className="text-sm text-muted-foreground text-center py-8">Loading…</div>}
        {data && !data.key_configured && (
          <div className="flex items-center gap-2 text-sm text-amber-600" data-testid="data-usage-no-key">
            <AlertTriangle className="w-4 h-4" /> No FMP key is set — every lookup uses the free backup.
          </div>
        )}
        {data && <UsageMeter data={data} />}
        {data && (
          <div>
            <h3 className="font-medium mb-2">Tickers that used the free backup <span className="text-muted-foreground font-normal">({data.fallbacks.length})</span></h3>
            <FallbackTable rows={data.fallbacks} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
